import React, { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { PostPreviewModal, FunnelPeopleModal, CheckoutStepsModal, fmtViews } from './OrganicPostModals';
import organicFetch from '../lib/organicFetch';
import { sessionHeaders } from '../services/auth';

// Unified cross-platform Performance table for the Social Media Builder. Merges
// Facebook post-insights (engagement + orders/revenue) with Instagram ig-insights
// (engagement only) into one table with a Platform column. Instagram captions
// can't carry a trackable link, so the order/revenue columns are blank for IG.
// TikTok analytics need the Display API (gated behind app audit) — omitted for now.

const BRANDS = [{ slug: 'crabplace', name: 'Crab Place' }, { slug: 'crabandcruise', name: 'Crab & Cruise' }];
const PLATFORM = {
  facebook:  { color: '#1877F2', label: 'FB' },
  instagram: { color: '#E1306C', label: 'IG' },
  igstory:   { color: '#F77737', label: 'IG Story' },
  tiktok:    { color: '#111111', label: 'TikTok' },
};
const ghostBtn = { background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'Montserrat', padding: '6px 12px' };
const money = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const post = (action, brand) => organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, brand }) }).then(r => r.json()).catch(() => ({}));

// Poster's initials from createdBy (the logged-in name at publish), e.g.
// "Greg Cain" -> "GC". Blank if there's no usable name.
function initialsOf(name) {
  const s = String(name || '').trim();
  if (!s || !/[a-z]/i.test(s)) return '';
  const parts = s.split(/\s+/).filter(Boolean);
  return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function PlatformCell({ platform }) {
  const p = PLATFORM[platform] || PLATFORM.facebook;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: 'var(--navy)' }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: p.color, display: 'inline-block', flexShrink: 0 }} />{p.label}
    </span>
  );
}

export default function UnifiedPerformance() {
  const [brand, setBrand] = useState('crabplace');
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('idle'); // idle | loading | ok | error
  const [kind, setKind] = useState('all'); // all | posts | stories
  const [preview, setPreview] = useState(null); // FB post row shown in the overlay
  const [people, setPeople] = useState(null);   // funnel drill-in (FB)
  const [steps, setSteps] = useState(null);     // checkout-step drop-off (FB)

  const load = useCallback(() => {
    setState('loading');
    // Force an attribution sync FIRST (join fresh checkout telemetry into
    // order_attribution) so a just-placed order is matched to its post without
    // waiting for the 5-min cron. Best-effort — never blocks the perf read.
    const recover = fetch('/.netlify/functions/neon', {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
      body: JSON.stringify({ query: 'order_attribution_recover_from_telemetry', params: { since_ms: Date.now() - 3 * 86400000 } }),
    }).catch(() => {});
    recover
      .then(() => Promise.all([post('post-insights', brand), post('ig-insights', brand)]))
      .then(([fb, ig]) => {
        const fbRows = (Array.isArray(fb.posts) ? fb.posts : []).map(p => ({ ...p, platform: 'facebook' }));
        const igRows = (Array.isArray(ig.posts) ? ig.posts : []).map(p => ({ ...p, platform: 'instagram' }));
        const storyRows = (Array.isArray(ig.stories) ? ig.stories : []).map(p => ({ ...p, platform: 'igstory' }));
        const merged = [...fbRows, ...igRows, ...storyRows].sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0));
        setRows(merged); setState('ok');
      })
      .catch(() => setState('error'));
  }, [brand]);
  useEffect(() => { load(); }, [load]);

  // Funnel drill-in (Facebook only — IG has no trackable link).
  const openPeople = (p) => {
    if (!p.utmContent) return;
    setPeople({ caption: p.caption, utmContent: p.utmContent, loading: true, list: [] });
    organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'funnel-people', utmContent: p.utmContent }) })
      .then((r) => r.json())
      .then((d) => setPeople((prev) => (prev && prev.utmContent === p.utmContent ? { ...prev, loading: false, list: d.people || [] } : prev)))
      .catch(() => setPeople((prev) => (prev ? { ...prev, loading: false, list: [] } : prev)));
  };

  // Header sticks to the top of the table's OWN scroll container (below), not
  // the page — page-level sticky (top:56) let rows slide up past the header and
  // peek out above it, which looked broken.
  const th = { textAlign: 'right', padding: '8px', fontWeight: 700, color: 'var(--gray-500)', fontSize: 11, position: 'sticky', top: 0, zIndex: 3, background: 'var(--gray-50)', boxShadow: 'inset 0 -1px 0 var(--gray-200)' };
  const td = { textAlign: 'right', padding: '8px', fontSize: 12 };
  const linkBtn = { background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--orange)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' };
  const dash = <span style={{ color: 'var(--gray-300, #c8ccd0)' }}>—</span>;

  return (
    <div>
      {/* Brand switcher (mirrors the builders) */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3, marginRight: 4 }}>Brand</span>
        {BRANDS.map((b) => (
          <button key={b.slug} onClick={() => setBrand(b.slug)}
            style={{ fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 16, cursor: 'pointer', fontFamily: 'Montserrat', background: brand === b.slug ? 'var(--navy)' : '#fff', color: brand === b.slug ? '#fff' : 'var(--navy)', border: `1px solid ${brand === b.slug ? 'var(--navy)' : 'var(--gray-200)'}` }}>
            {b.name}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Post performance <span style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--gray-500)' }}>· Facebook &amp; Instagram</span></span>
          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {[['all', 'All'], ['posts', 'Posts'], ['stories', 'Stories']].map(([k, label]) => (
              <button key={k} onClick={() => setKind(k)}
                style={{ ...ghostBtn, padding: '5px 10px', background: kind === k ? 'var(--navy)' : '#fff', color: kind === k ? '#fff' : 'var(--navy)' }}>{label}</button>
            ))}
            <button onClick={load} style={ghostBtn}>Refresh</button>
          </span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {state === 'loading' || state === 'idle' ? (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}><Loader2 size={13} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />Loading post performance…</div>
          ) : state === 'error' ? (
            <div style={{ padding: 16, fontSize: 12, color: '#c33' }}>Couldn't load performance right now.</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No published posts yet — performance shows once a post goes live.</div>
          ) : (
            <div style={{ overflow: 'auto', maxHeight: '72vh' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ ...th, textAlign: 'left', padding: '8px 12px' }}>Post</th>
                    <th style={th}>Date</th>
                    <th style={{ ...th, textAlign: 'left' }}>Platform</th>
                    <th style={th}>Views</th><th style={th}>Likes</th><th style={th}>Comments</th><th style={th}>Clicks</th><th style={th}>Carts</th><th style={th}>Checkout</th>
                    <th style={th}>Orders</th><th style={th}>New / Repeat</th><th style={th}>Signups</th><th style={{ ...th, padding: '8px 12px' }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.filter(p => kind === 'all' || (kind === 'stories') === (p.platform === 'igstory')).map(p => {
                    const isFb = p.platform === 'facebook';
                    const isStory = p.platform === 'igstory';
                    const orders = (p.ordersActive ?? p.orders);
                    // IG rows open the same preview/comments modal as FB (Rebecca's
                    // ask) — fbPostId carries the IG media id for the ig-* actions.
                    const rowClick = () => {
                      if (isFb) setPreview(p);
                      else if (isStory) { if (p.live && p.permalink) window.open(p.permalink, '_blank', 'noopener'); }
                      else if (p.platform === 'instagram' && p.mediaId) setPreview({ ...p, fbPostId: p.mediaId });
                      else if (p.permalink) window.open(p.permalink, '_blank', 'noopener');
                    };
                    return (
                      <tr key={`${p.platform}-${p.id}`} onClick={rowClick} title={isFb ? 'Click to preview the Facebook post' : 'Click to preview the Instagram post'}
                        style={{ borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--gray-50)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                        <td style={{ padding: '8px 12px', maxWidth: 320 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {p.thumb ? <img src={p.thumb} alt="" style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover', flexShrink: 0, background: '#000' }} /> : null}
                            <div style={{ minWidth: 0 }}>
                              <span style={{ fontSize: 11.5, color: 'var(--navy)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{p.caption || '(no caption)'}</span>
                              {initialsOf(p.createdBy) && <div title={`Posted by ${p.createdBy}`} style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 2, fontWeight: 700 }}>By {initialsOf(p.createdBy)}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 11, color: 'var(--gray-500)' }}>{p.publishedAt ? (() => { const d = new Date(p.publishedAt); return (<>{d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}<div style={{ fontSize: 9.5, color: 'var(--gray-400)' }}>{d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div></>); })() : '—'}</td>
                        <td style={{ ...td, textAlign: 'left' }}><PlatformCell platform={p.platform} /></td>
                        <td style={td}>{isFb && !p.views ? <span title="Facebook only gives us views on video posts">{dash}</span> : fmtViews(p.views)}</td>
                        <td style={td}>{isStory ? dash : (p.likes || 0).toLocaleString()}</td>
                        <td style={td} title={isStory ? 'Replies to the story' : undefined}>{(p.comments || 0).toLocaleString()}</td>
                        <td style={td}>{isFb ? (p.clicks || 0).toLocaleString() : dash}</td>
                        <td style={td}>{isFb ? (p.carts ? <button onClick={(e) => { e.stopPropagation(); openPeople(p); }} style={linkBtn} title="See the people in this post's funnel">{p.carts.toLocaleString()}</button> : 0) : dash}</td>
                        <td style={td}>{isFb ? (p.checkouts ? <button onClick={(e) => { e.stopPropagation(); setSteps({ caption: p.caption, utmContent: p.utmContent, checkouts: p.checkouts }); }} style={linkBtn} title="See how far these checkout sessions got toward an order">{p.checkouts.toLocaleString()}</button> : 0) : dash}</td>
                        <td style={{ ...td, fontWeight: 700, color: orders ? 'var(--navy)' : 'var(--gray-500)' }}>
                          {!isFb ? dash : (p.ordersActive != null && p.ordersActive !== p.orders)
                            ? <span title={`${p.orders} placed, ${p.ordersActive} currently valid (rest canceled or deleted)`}>
                                <span style={{ color: 'var(--gray-400)', fontWeight: 600, textDecoration: 'line-through' }}>{p.orders}</span>
                                <span style={{ color: 'var(--gray-400)' }}> / </span>
                                <span style={{ color: p.ordersActive ? 'var(--navy)' : 'var(--gray-500)' }}>{p.ordersActive}</span>
                              </span>
                            : (p.orders || 0)}
                        </td>
                        <td style={td}>{!isFb ? dash : (orders ? `${p.firstTime || 0} / ${p.repeatCust || 0}` : '—')}</td>
                        <td style={td}>{isFb ? (p.signups || 0).toLocaleString() : dash}</td>
                        <td style={{ ...td, padding: '8px 12px', fontWeight: 600, color: orders ? '#1a8a4a' : 'var(--gray-500)' }}>{!isFb ? dash : (orders ? money(p.revenue) : (p.orders ? money(0) : '—'))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ fontSize: 10.5, color: 'var(--gray-500)', padding: '8px 12px', lineHeight: 1.5 }}>
                Likes / comments / views come from each platform. Facebook views show on video posts only; photo posts show a dash. <strong>Facebook</strong> rows also track orders: <strong>Clicks</strong> = real visits to the post's tracked crabplace.co link, <strong>Carts</strong> / <strong>Checkout</strong> / <strong>Signups</strong> = visitors who added to cart, reached checkout, or joined the newsletter, and <strong>Orders</strong> / <strong>New&nbsp;/&nbsp;Repeat</strong> / <strong>Revenue</strong> are matched by the post's UTM tag. <strong>Instagram</strong> captions can't carry a clickable link, so those columns stay blank (engagement only). Click a Facebook row to preview the post; click a <strong>Carts</strong> number to see the people in that funnel, or a <strong>Checkout</strong> number to see how far those sessions got toward an order (address &rarr; delivery date &amp; shipping &rarr; credit card &rarr; placed). Click an Instagram row to open it on Instagram. <strong>IG Story</strong> rows are saved every hour while the story is live (Instagram drops a story and its numbers after 24 hours): Views, and Comments = replies. Posts made straight in the Facebook or Instagram app show here too. <strong>TikTok</strong> analytics arrive once posting is approved (they need TikTok's Display API).
              </div>
            </div>
          )}
        </div>
      </div>

      {preview && <PostPreviewModal post={preview} onClose={() => setPreview(null)} />}
      {people && <FunnelPeopleModal data={people} onClose={() => setPeople(null)} />}
      {steps && <CheckoutStepsModal data={steps} onClose={() => setSteps(null)} />}
    </div>
  );
}
